// Helper functions for chapter manipulation in markdown

export interface ChapterInfo {
  startLine: number;
  endLine: number;
  title: string;
}

/**
 * Extract chapter content from markdown
 */
export function extractChapterContent(
  fullMarkdown: string,
  chapterIndex: number
): { title: string; content: string } | null {
  const lines = fullMarkdown.split('\n');
  const chapters: ChapterInfo[] = [];
  
  let currentChapterStart = -1;
  let currentTitle = '';
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    // Match chapter headers: "# Chapter N: Title" or "# Title"
    const chapterMatch = line.match(/^#\s*Chapter\s+\d+\s*:\s*(.+)/i) || line.match(/^#\s*(.+)/);
    
    if (chapterMatch) {
      if (currentChapterStart !== -1) {
        chapters.push({
          startLine: currentChapterStart,
          endLine: i - 1,
          title: currentTitle
        });
      }
      currentChapterStart = i;
      currentTitle = chapterMatch[1].trim();
    }
  }
  
  // Add the last chapter
  if (currentChapterStart !== -1) {
    chapters.push({
      startLine: currentChapterStart,
      endLine: lines.length - 1,
      title: currentTitle
    });
  }
  
  if (chapterIndex < 0 || chapterIndex >= chapters.length) return null;
  
  const chapter = chapters[chapterIndex];
  const contentLines = lines.slice(chapter.startLine + 1, chapter.endLine + 1);
  
  return {
    title: chapter.title,
    content: contentLines.join('\n').trim()
  };
}

/**
 * Replace chapter content in markdown
 */
export function replaceChapterContent(
  fullMarkdown: string,
  chapterIndex: number,
  newTitle: string,
  newContent: string
): string {
  const lines = fullMarkdown.split('\n');
  const chapterStarts: number[] = [];
  
  // Find all chapter start lines
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.match(/^#\s*Chapter\s+\d+/i) || line.match(/^#\s+[^#]/)) {
      chapterStarts.push(i);
    }
  }
  
  if (chapterIndex < 0 || chapterIndex >= chapterStarts.length) return fullMarkdown;
  
  const startLine = chapterStarts[chapterIndex];
  const endLine = chapterIndex < chapterStarts.length - 1 ? chapterStarts[chapterIndex + 1] - 1 : lines.length - 1;
  
  // Get the sequence number from the original header
  const originalHeader = lines[startLine];
  const sequenceMatch = originalHeader.match(/Chapter\s+(\d+)/i);
  const sequence = sequenceMatch ? sequenceMatch[1] : (chapterIndex + 1).toString();
  
  // Build new chapter
  const newChapterLines = [
    `# Chapter ${sequence}: ${newTitle}`,
    '',
    ...newContent.split('\n'),
    ''
  ];
  
  // Replace the chapter in the full markdown
  const beforeChapter = lines.slice(0, startLine);
  const afterChapter = lines.slice(endLine + 1);
  
  return [...beforeChapter, ...newChapterLines, ...afterChapter].join('\n');
}

/**
 * Parse all chapters from markdown
 */
export function parseChapters(fullMarkdown: string): ChapterInfo[] {
  const lines = fullMarkdown.split('\n');
  const chapters: ChapterInfo[] = [];
  
  let currentChapterStart = -1;
  let currentTitle = '';
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const chapterMatch = line.match(/^#\s*Chapter\s+\d+\s*:\s*(.+)/i) || line.match(/^#\s*(.+)/);
    
    if (chapterMatch) {
      if (currentChapterStart !== -1) {
        chapters.push({
          startLine: currentChapterStart,
          endLine: i - 1,
          title: currentTitle
        });
      }
      currentChapterStart = i;
      currentTitle = chapterMatch[1].trim();
    }
  }
  
  // Add the last chapter
  if (currentChapterStart !== -1) {
    chapters.push({
      startLine: currentChapterStart,
      endLine: lines.length - 1,
      title: currentTitle
    });
  }
  
  return chapters;
}
